import { Controller } from '@hotwired/stimulus';
import Quill from 'quill';
import QuillMarkdown from 'quilljs-markdown';
import mention from 'quill-mention/autoregister';

export default class extends Controller {

  static targets = ['container', 'input'];

  static values = {
    inline: Boolean,
    jwt: String,
    contextType: String,
    contextId: String,
    allowTagEveryone: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  }

  connect() {
    Quill.register('modules/QuillMarkdown', QuillMarkdown, true);
    const jwt = this.jwtValue;
    const hasMentions = jwt !== null && jwt !== undefined && jwt !== '';
    const contextType = this.contextTypeValue;
    const contextId = this.contextIdValue;
    const allowTagEveryone = this.allowTagEveryoneValue;
    const quill = new Quill(this.containerTarget, {
      modules: {
        toolbar: false,
        keyboard: {
          bindings: {
            enter: {
              key: 'Enter',
              handler: (range, context) => {
                if (this.inlineValue) {
                  this.inputTarget.closest('form').requestSubmit();
                } else {
                  quill.insertText(range.index, '\n');
                }
              }
            },
          }
        },
        QuillMarkdown: {},
        mention: {
          allowedChars: /^[A-Za-z0-9\sÅÄÖåäö]*$/,
          mentionDenotationChars: ["@"],
          source: function (searchTerm, renderList, mentionChar) {
            if (hasMentions) {
              const params = new URLSearchParams({
                jwt: jwt,
                query: searchTerm
              });

              if (contextType) {
                params.append("context_type", contextType);
              }
              if (contextId) {
                params.append("context_id", contextId);
              }
              if (!allowTagEveryone) {
                params.append("allow_tag_everyone", "false");
              }

              fetch(`/private_api/member_mentions?${params}`)
                .then(function (response) {
                  return response.json();
                })
                .then(function (data) {
                  renderList(data, searchTerm);
                })
                .catch(function (error) {
                  console.error("Error fetching mentioned users:", error);
                });
            }
          },
        }

      },
      readOnly: false,
      theme: 'snow',
      formats: this.buildFormats()
    });

    const input = this.inputTarget;

    if (input.value !== null && input.value !== undefined && input.value !== '') {
      quill.clipboard.dangerouslyPasteHTML(input.value);
    }

    quill.on('text-change', (delta, oldDelta, source) => {
      if (source === 'user') {
        this.autoLinkUrls(quill, delta);
      }

      if (quill.getText().trim().length === 0) {
        input.value = '';
      } else {
        input.value = quill.getSemanticHTML()
          .replaceAll('<p></p>', '<p><br/></p>')
          .replaceAll('&nbsp;', ' ');
      }
    });

    this.quillObject = quill;

    if (this.autofocusValue) {
      setTimeout(() => {
        quill.focus();
        const length = quill.getLength();
        quill.setSelection(length, 0);
      }, 0);

      this.focusHandler = this.focus.bind(this);
      window.addEventListener('richEditor:focusInput', this.focusHandler);
    }
  }

  disconnect() {
    if (this.autofocusValue) {
      window.removeEventListener('richEditor:focusInput', this.focusHandler);
    }
  }

  focus() {
    const length = this.quillObject.getLength();
    this.quillObject.focus();
    this.quillObject.setSelection(length, 0);
  }

  autoLinkUrls(quill, delta) {
    const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi;

    const text = quill.getText();
    const matches = [];
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        url: match[0]
      });
    }

    matches.forEach(({ index, length, url }) => {
      const format = quill.getFormat(index, length);

      if (!format.link) {
        quill.formatText(index, length, 'link', url, 'silent');
      }
    });
  }

  buildFormats() {
    if (this.inlineValue) {
      return [
        'bold',
        'italic',
        'list',
        'link',
        'mention'
      ]
    } else {
      return [
        'bold',
        'italic',
        'list',
        'link',
        'header',
        'mention'
      ]
    }
  }

  handleChange(event) {
    this.quillObject.root.innerHTML = event.detail.content;
  }

}